import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { supabase } from '../../config/supabase'
import { posts as staticPosts, type Post } from './posts'
import PostRenderer from './PostRenderer'

const PostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<Post | null>(null)
  const [status, setStatus] = useState<'loading' | 'found' | 'notfound'>('loading')

  useEffect(() => {
    if (!slug) { setStatus('notfound'); return }

    const load = async () => {
      // Try Supabase first
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single()

      if (!error && data) {
        setPost(data as Post)
        setStatus('found')
        return
      }

      // Fall back to static posts
      const found = staticPosts.find((p) => p.slug === slug)
      if (found) {
        setPost(found)
        setStatus('found')
      } else {
        setStatus('notfound')
      }
    }

    load()
  }, [slug])

  useEffect(() => {
    if (!post) return
    const prev = document.title
    document.title = `${post.title} — Jax`
    window.scrollTo(0, 0)
    return () => { document.title = prev }
  }, [post])

  const shell = (children: React.ReactNode) => (
    <div
      className="min-h-screen bg-jax-bg text-white"
      style={{ fontFamily: '"Source Sans Pro", system-ui, sans-serif' }}
    >
      <div className="h-[2px] bg-jax-accent" />
      <header className="border-b border-jax-navy">
        <nav className="max-w-[680px] mx-auto px-6 py-4">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 font-mono text-[11px] text-jax-subtle hover:text-jax-link transition-colors duration-200 tracking-wide"
          >
            <ArrowLeft className="w-3 h-3" />
            back
          </Link>
        </nav>
      </header>
      <main className="max-w-[680px] mx-auto px-6 pb-24">
        {children}
      </main>
    </div>
  )

  if (status === 'loading') return shell(
    <div className="pt-20 text-jax-subtle font-mono text-sm animate-pulse">loading...</div>
  )

  if (status === 'notfound' || !post) return shell(
    <div className="pt-20">
      <p className="text-jax-subtle font-mono text-sm mb-4">post not found</p>
      <Link to="/about" className="font-mono text-[11px] text-jax-link">← back to about</Link>
    </div>
  )

  return shell(
    <>
      <section className="pt-14 pb-10 border-b border-jax-navy">
        <div className="flex items-center gap-4 mb-5">
          <span className="text-[11px] font-mono text-jax-subtle">{post.date}</span>
          <span className="text-[11px] font-mono text-jax-subtle">{post.readTime} read</span>
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight mb-4 select-none"
          onDoubleClick={() => navigate(`/editor?slug=${slug}`)}
        >
          {post.title}
        </h1>
        <p className="text-jax-link font-mono text-sm">{post.subtitle}</p>
      </section>

      <PostRenderer post={post} />

      <div className="mt-16 pt-8 border-t border-jax-navy">
        <Link
          to="/about"
          className="inline-flex items-center gap-2 font-mono text-[11px] text-jax-subtle hover:text-jax-link transition-colors duration-200 tracking-wide"
        >
          <ArrowLeft className="w-3 h-3" />
          back to about
        </Link>
      </div>
    </>
  )
}

export default PostPage
